"use client";

import React, { useEffect, useState } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Users, Clock, AlertTriangle, Briefcase } from "lucide-react";
const statsData = [{
  id: 1,
  label: "Waiting for spine or pain care",
  value: 40000,
  suffix: "",
  icon: Users,
  isText: false
}, {
  id: 2,
  label: "Typical wait, up to",
  value: 10,
  suffix: " months",
  icon: Clock,
  isText: false
}, {
  id: 3,
  label: "Waiting over a year",
  value: 0,
  suffix: "",
  icon: AlertTriangle,
  isText: true,
  textValue: "1 in 4"
}, {
  id: 4,
  label: "Unable to work due to pain",
  value: 12,
  suffix: "%",
  icon: Briefcase,
  isText: false
}];
const problemCards = [{
  title: "40,000 people still waiting",
  description: "More than 40,000 people in Ireland are waiting for neurosurgery or pain management (NTPF, July 2026). Over 11,000 have been waiting more than a year."
}, {
  title: "Delay makes it worse",
  description: "Spine and chronic pain conditions worsen without treatment. Nerve damage can become permanent. Waiting is not a neutral option."
}, {
  title: "The HSE can cover your costs",
  description: "Through the Cross-Border Directive, HSE reimburses up to €8,558 for spine surgery and up to €874 for pain procedures at Clinic Agatas."
}];
function AnimatedCounter({
  end,
  suffix = "",
  isVisible
}: {
  end: number;
  suffix?: string;
  isVisible: boolean;
}) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!isVisible) return;
    let start = 0;
    const duration = 2000;
    const fps = 60;
    const increment = end / (duration / (1000 / fps));
    const timer = setInterval(() => {
      start += increment;
      if (start >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 1000 / fps);
    return () => clearInterval(timer);
  }, [end, isVisible]);
  return <span className="tabular-nums">{count.toLocaleString("en-IE")}{suffix}</span>;
}
export default function Authority() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="203715" id="problemawareness" className="bg-[#eefaf4] pb-0">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={ref} className={`relative rounded-lg overflow-hidden bg-[#eefaf4] text-white shadow-md transition-all duration-1000 transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,var(--color-primary-400)_0%,transparent_50%)] opacity-10 pointer-events-none"></div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,var(--color-primary-600)_0%,transparent_50%)] opacity-20 pointer-events-none"></div>
          <div className="absolute inset-4 sm:inset-6 border border-primary-foreground/5 rounded-md pointer-events-none"></div>

          <div className="relative z-10 flex flex-col items-center text-center px-4 sm:px-10 md:px-16 lg:px-24 py-10 md:py-12">

            <div className="max-w-5xl mx-auto mb-10 md:mb-16">
              <h2 className="font-default text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight mb-4 md:mb-6 leading-tight text-[#264256]">Almost 1 million on HSE waiting lists.<div>Over 40,000 waiting for neurosurgery or pain treatment.</div><div>You do not have to be one of them.</div></h2>
              <p className="text-base sm:text-xl text-[#264256] leading-relaxed max-w-2xl mx-auto font-default">
                If you are one of them, you already know what waiting feels like. The pain does not pause while the system catches up.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-10 md:mb-20 w-full max-w-6xl">
              {problemCards.map((card, i) => <div key={i} data-index={i} className="bg-white rounded-lg border border-[#f1f3f5] shadow-xl p-6 flex flex-col gap-3 text-left">
                  <h3 className="font-default font-bold text-[#264256] text-lg md:text-xl leading-snug">{card.title}</h3>
                  <p className="font-default text-[#264256] text-sm md:text-base leading-relaxed">{card.description}</p>
                </div>)}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-12 w-full max-w-5xl pb-0">
              {statsData.map((stat, i) => <div key={stat.id} data-index={i} className="flex flex-col items-center p-3 md:p-4 text-center h-full !justify-start">
                  <div className="mb-3 md:mb-4 p-3 md:p-4 bg-[#264256] rounded-full border border-primary-foreground/10 backdrop-blur-sm">
                    <stat.icon className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 text-accent" strokeWidth={1.5} />
                  </div>
                  <div className="font-default text-3xl sm:text-4xl md:text-5xl font-bold mb-2 tracking-tight text-[#264256]">
                    {stat.isText ? <span>{stat.textValue}</span> : <AnimatedCounter end={stat.value} suffix={stat.suffix} isVisible={isVisible} />}
                  </div>
                  <div className="text-xs sm:text-sm font-semibold text-[#264256] uppercase tracking-widest font-default text-center">
                    {stat.label}
                  </div>
                </div>)}
            </div>

          </div>
        </div>
      </div>
    </section>;
}